import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Button,
  Space,
  message,
  Row,
  Col,
  Typography,
  Breadcrumb,
  Table,
  Checkbox,
  Spin,
  Modal
} from 'antd';
import {
  SaveOutlined,
  UserOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import EmployeeSelect from '../components/common/EmployeeSelect';
import MessagePreview from '../components/sop/MessagePreview';
import AttachmentManager from '../components/sop/AttachmentManager';
import weworkContactWayService from '../services/weworkContactWayService';
import tagService from '../services/tagService';
import fileService from '../services/fileService';
import './WeworkContactWay.css';

const { Title, Text, Paragraph } = Typography;

const WeworkContactWayBatchEdit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // 活码列表和选择相关状态
  const [contactWays, setContactWays] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  
  // 编辑选项状态
  const [editOptions, setEditOptions] = useState({
    editMembers: false,      // 是否编辑使用成员
    editWelcomeMsg: false    // 是否编辑欢迎语
  });
  
  // 批量欢迎语替换范围（按类型精细替换）
  const [welcomeReplaceOptions, setWelcomeReplaceOptions] = useState({
    text: false,
    imageGroup: false,
    video: false,
    miniprogram: false,
    link: false
  });
  
  // 附件管理相关状态
  const [attachments, setAttachments] = useState([
    { id: 'text_1', type: 'TEXT', content: '' }
  ]);
  const [previewUrls, setPreviewUrls] = useState({});
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [nextAttachmentId, setNextAttachmentId] = useState(2);
  const [previewStep, setPreviewStep] = useState(null);

  // 初始化选中活码（由上级页面传入）
  useEffect(() => {
    const state = location.state || {};
    const ids = Array.isArray(state.selectedIds) ? state.selectedIds : [];
    const items = Array.isArray(state.selectedItems) ? state.selectedItems : [];
    if (!ids || ids.length === 0) {
      message.warning('请先在列表中选择要批量编辑的活码');
      navigate('/wework-contact-way');
      return;
    }
    setSelectedRowKeys(ids);
    setSelectedRows(items);
  }, []);

  // 处理编辑选项变化
  const handleEditOptionChange = (option, checked) => {
    setEditOptions(prev => ({
      ...prev,
      [option]: checked
    }));
  };

  // 此页面不提供行选择，使用上级页面已选择的结果

  // 处理文件上传
  const handleFileUpload = async ({ file, onSuccess, onError, onProgress, fieldKey, mediaType, useTempMedia = true, stepType = 'MESSAGE' }) => {
    try {
      const uploadKey = `${stepType}_${fieldKey}`;
      setUploading(prev => ({ ...prev, [uploadKey]: true, [fieldKey]: true }));
      setUploadProgress(prev => ({ ...prev, [uploadKey]: 0, [fieldKey]: 0 }));

      const updateProgress = (percent) => {
        setUploadProgress(prev => ({ ...prev, [uploadKey]: percent, [fieldKey]: percent }));
        if (onProgress) {
          onProgress({ percent });
        }
      };

      const tenantStr = localStorage.getItem('tenant');
      if (!tenantStr) {
        message.error('未获取到租户信息，请重新登录');
        if (onError) onError(new Error('未获取到租户信息'));
        return;
      }
      const tenant = JSON.parse(tenantStr);
      const tenantId = tenant.id;

      const result = await fileService.uploadMedia(file, mediaType, tenantId, updateProgress);

      if (result.success && result.mediaId) {
        console.log(`媒体上传成功 - 字段: ${fieldKey}, mediaId: ${result.mediaId}, ossUrl: ${result.ossUrl}`);

        setPreviewUrls(prev => ({
          ...prev,
          [result.mediaId]: result.ossUrl
        }));

        message.success(`${file.name} 上传成功`);

        if (onSuccess) {
          onSuccess(result);
        }
        
        return result;
      } else {
        const errorMsg = result?.message || '上传失败';
        message.error(errorMsg);
        if (onError) onError(new Error(errorMsg));
      }
    } catch (error) {
      console.error('文件上传失败:', error);
      message.error(`${file.name} 上传失败: ${error.message}`);
      if (onError) onError(error);
    } finally {
      const uploadKey = `${stepType}_${fieldKey}`;
      setUploading(prev => ({ ...prev, [uploadKey]: false, [fieldKey]: false }));
    }
  };

  // 处理文件移除
  const handleRemoveFile = (fieldKey, stepType = 'MESSAGE') => {
    console.log(`移除文件 - 字段: ${fieldKey}, 步骤类型: ${stepType}`);
    const attachmentToUpdate = attachments.find(att => att.id === fieldKey);
    if (attachmentToUpdate && attachmentToUpdate.mediaId) {
      setPreviewUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[attachmentToUpdate.mediaId];
        return newUrls;
      });
    }
  };

  // 添加附件
  const handleAddAttachment = (type) => {
    // 防止重复创建图片组：若已存在则复用
    if (type === 'IMAGE_GROUP') {
      const existing = attachments.find(att => att.type === 'IMAGE_GROUP');
      if (existing) {
        return existing.id;
      }
    }

    // 生成更加唯一的ID，避免并发下重复
    const uniq = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const newId = `attachment_${uniq}`;

    const newAttachment = {
      id: newId,
      type: type,
      content: '',
      ...(type === 'IMAGE_GROUP' && { imageGroup: { mediaIds: [], ossUrls: {} } }),
      ...(type === 'VIDEO' && { mediaId: '', ossUrl: '' }),
      ...(type === 'LINK' && { title: '', description: '', url: '' }),
      ...(type === 'MINIPROGRAM' && { title: '', appid: '', pagepath: '', thumbMediaId: '', thumbMediaId_ossUrl: '' }),
      ...(type === 'FILE' && { mediaId: '', ossUrl: '' })
    };

    setAttachments(prev => [...prev, newAttachment]);
    setNextAttachmentId(prev => prev + 1);
    return newId;
  };

  // 移除附件
  const handleRemoveAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(att => att.id !== attachmentId));
    
    const attachmentToRemove = attachments.find(att => att.id === attachmentId);
    if (attachmentToRemove) {
      if (attachmentToRemove.mediaId) {
        setPreviewUrls(prev => {
          const newUrls = { ...prev };
          delete newUrls[attachmentToRemove.mediaId];
          return newUrls;
        });
      }
      if (attachmentToRemove.imageGroup && attachmentToRemove.imageGroup.mediaIds) {
        setPreviewUrls(prev => {
          const newUrls = { ...prev };
          attachmentToRemove.imageGroup.mediaIds.forEach(mediaId => {
            delete newUrls[mediaId];
          });
          return newUrls;
        });
      }
    }
  };

  // 更新附件内容
  const handleUpdateAttachmentContent = (attachmentId, field, value) => {
    setAttachments(prev => prev.map(att =>
      att.id === attachmentId ? { ...att, [field]: value } : att
    ));
  };

  // 插入客户昵称
  const insertNickname = () => {
    setAttachments(prev => {
      const list = Array.isArray(prev) ? prev.slice() : [];
      const idx = list.findIndex(a => a && a.type === 'TEXT');
      if (idx === -1) return [{ id: `text-${Date.now()}`, type: 'TEXT', content: '%NICKNAME%' }, ...list];
      const target = list[idx];
      const before = target.content || '';
      list[idx] = { ...target, content: `${before}%NICKNAME%` };
      return list;
    });
  };

  // 更新预览
  const updatePreview = () => {
    const textAttachment = attachments.find(att => att.type === 'TEXT');
    const otherAttachments = attachments.filter(att => att.type !== 'TEXT');
    
    const attachmentList = [];
    
    if (textAttachment?.content) {
      attachmentList.push({
        msgtype: 'text',
        text: {
          content: textAttachment.content
        }
      });
    }
    
    otherAttachments.forEach(att => {
      if (att.type === 'IMAGE_GROUP' && att.imageGroup && att.imageGroup.mediaIds && att.imageGroup.mediaIds.length > 0) {
        att.imageGroup.mediaIds.forEach(mediaId => {
          attachmentList.push({
            msgtype: 'image',
            image: {
              media_id: mediaId,
              oss_url: att.imageGroup.ossUrls[mediaId] || ''
            }
          });
        });
      } else if (att.type === 'VIDEO' && att.mediaId) {
        attachmentList.push({
          msgtype: 'video',
          video: {
            media_id: att.mediaId,
            oss_url: att.ossUrl || ''
          }
        });
      } else if (att.type === 'LINK' && att.url) {
        attachmentList.push({
          msgtype: 'link',
          link: {
            title: att.title || '',
            desc: att.description || '',
            url: att.url,
            picurl: '',
            pic_oss_url: ''
          }
        });
      } else if (att.type === 'MINIPROGRAM' && att.appid && att.title) {
        attachmentList.push({
          msgtype: 'miniprogram',
          miniprogram: {
            title: att.title,
            appid: att.appid,
            page: att.pagepath || '',
            pic_media_id: att.thumbMediaId || '',
            pic_oss_url: att.thumbMediaId_ossUrl || ''
          }
        });
      } else if (att.type === 'FILE' && att.mediaId) {
        attachmentList.push({
          msgtype: 'file',
          file: {
            media_id: att.mediaId,
            oss_url: att.ossUrl || ''
          }
        });
      }
    });
    
    const step = {
      actionType: 'SEND_MESSAGE',
      actionParams: {
        text: textAttachment?.content || '',
        attachments: attachmentList
      }
    };
    
    setPreviewStep(step);
  };

  // 监听附件变化以更新预览
  useEffect(() => {
    updatePreview();
  }, [attachments]);

  // 表格列定义
  const columns = [
    {
      title: '活码名称',
      dataIndex: 'remark',
      key: 'remark',
      width: 250,
      render: (text) => text || '未命名活码'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <span className={status === 1 ? 'status-active' : 'status-inactive'}>
          {status === 1 ? '正常' : '已失效'}
        </span>
      )
    },
    {
      title: '使用成员数',
      dataIndex: 'userIds',
      key: 'userCount',
      width: 120,
      render: (userIds) => {
        if (!userIds) return 0;
        
        if (Array.isArray(userIds)) {
          return userIds.length;
        }
        
        if (typeof userIds === 'string') {
          try {
            const parsedUserIds = JSON.parse(userIds);
            return Array.isArray(parsedUserIds) ? parsedUserIds.length : 0;
          } catch (error) {
            return 0;
          }
        }
        
        return 0;
      }
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (time) => time ? new Date(time).toLocaleString() : '-'
    }
  ];

  // 批量更新活码
  const handleSubmit = async (values) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要编辑的活码');
      return;
    }

    if (!editOptions.editMembers && !editOptions.editWelcomeMsg) {
      message.warning('请至少选择一项要编辑的内容');
      return;
    }

    setSaving(true);
    try {
      const updateFields = {};
      
      // 如果编辑使用成员
      if (editOptions.editMembers && values.userIds) {
        updateFields.user_ids = values.userIds;
      }
      
      // 如果编辑欢迎语
      if (editOptions.editWelcomeMsg) {
        // 校验是否至少选择了一种需要替换的类型
        const hasAnyReplace = welcomeReplaceOptions.text || welcomeReplaceOptions.imageGroup || welcomeReplaceOptions.video || welcomeReplaceOptions.miniprogram || welcomeReplaceOptions.link;
        if (!hasAnyReplace) {
          message.warning('请选择需要替换的欢迎语类型');
          setSaving(false);
          return;
        }
        const textAttachment = attachments.find(att => att.type === 'TEXT');
        const otherAttachments = attachments.filter(att => att.type !== 'TEXT');
        
        // 需要替换的类型集合（只提交被勾选的类型）
        const replaceTypes = [];
        if (welcomeReplaceOptions.imageGroup) replaceTypes.push('IMAGE_GROUP');
        if (welcomeReplaceOptions.video) replaceTypes.push('VIDEO');
        if (welcomeReplaceOptions.miniprogram) replaceTypes.push('MINIPROGRAM');
        if (welcomeReplaceOptions.link) replaceTypes.push('LINK');

        // 构建 attachments，仅包含勾选类型
        const filteredAttachments = otherAttachments
          .filter(att => replaceTypes.includes(att.type))
          .map(att => {
            if (att.type === 'IMAGE_GROUP') {
              return {
                type: 'IMAGE_GROUP',
                imageGroup: att.imageGroup
              };
            } else if (att.type === 'VIDEO') {
              return {
                type: 'VIDEO',
                mediaId: att.mediaId,
                ossUrl: att.ossUrl
              };
            } else if (att.type === 'LINK') {
              return {
                type: 'LINK',
                title: att.title,
                description: att.description,
                url: att.url
              };
            } else if (att.type === 'MINIPROGRAM') {
              return {
                type: 'MINIPROGRAM',
                title: att.title,
                appid: att.appid,
                pagepath: att.pagepath,
                thumbMediaId: att.thumbMediaId,
                thumbMediaId_ossUrl: att.thumbMediaId_ossUrl
              };
            } else if (att.type === 'FILE') {
              return {
                type: 'FILE',
                mediaId: att.mediaId,
                ossUrl: att.ossUrl
              };
            }
            return att;
          })
          .filter(att => {
            if (att.type === 'IMAGE_GROUP') {
              return att.imageGroup && att.imageGroup.mediaIds && att.imageGroup.mediaIds.length > 0;
            } else if (att.type === 'VIDEO' || att.type === 'FILE') {
              return att.mediaId;
            } else if (att.type === 'LINK') {
              return att.url;
            } else if (att.type === 'MINIPROGRAM') {
              return att.appid && att.title;
            }
            return false;
          });

        const welcomeData = {
          // 是否替换文本由勾选项决定
          ...(welcomeReplaceOptions.text ? { text: textAttachment?.content || '' } : {}),
          attachments: filteredAttachments,
          replace_types: replaceTypes,
          replace_text: !!welcomeReplaceOptions.text
        };

        // 提交前校验：非文本附件总数不能超过9
        const attachmentTotalCount = (filteredAttachments || []).reduce((sum, att) => {
          if (att.type === 'IMAGE_GROUP') {
            const imgs = att.imageGroup && Array.isArray(att.imageGroup.mediaIds) ? att.imageGroup.mediaIds.length : 0;
            return sum + imgs;
          }
          // VIDEO / LINK / MINIPROGRAM / FILE 等按1计
          return sum + 1;
        }, 0);
        if (attachmentTotalCount > 9) {
          message.error(`欢迎语附件总数不能超过9个（不含文本），当前为 ${attachmentTotalCount} 个`);
          setSaving(false);
          return;
        }
        updateFields.welcome_msg = welcomeData;
      }

      console.log('📤 批量更新请求数据:', {
        ids: selectedRowKeys,
        updateFields
      });

      const data = await weworkContactWayService.batchUpdateContactWays(
        selectedRowKeys,
        updateFields
      );

      const failures = Array.isArray(data?.failures) ? data.failures : [];
      const successCount = data?.successCount ?? 0;
      const totalCount = data?.totalCount ?? selectedRowKeys.length;

      if (failures.length > 0) {
        const topList = failures.slice(0, 20);
        const moreCount = failures.length - topList.length;
        const content = (
          <div>
            <div style={{ marginBottom: 8 }}>
              共 {totalCount} 个，成功 {successCount} 个，失败 {failures.length} 个。
            </div>
            <div style={{ maxHeight: 320, overflow: 'auto' }}>
              <ul style={{ paddingLeft: 20 }}>
                {topList.map((f, idx) => (
                  <li key={`${f.id || idx}`}>
                    <span style={{ color: '#333' }}>{f.id != null ? `ID ${f.id}` : '未知ID'}</span>
                    <span style={{ color: '#999' }}> ：</span>
                    <span style={{ color: '#cf1322' }}>{f.reason || '未知原因'}</span>
                  </li>
                ))}
              </ul>
              {moreCount > 0 && (
                <div style={{ color: '#999' }}>其余 {moreCount} 条省略…</div>
              )}
            </div>
          </div>
        );

        if (successCount > 0) {
          Modal.warning({
            title: '部分成功：部分更新失败，请检查参数或附件限制',
            content,
            width: 720
          });
          message.warning(`部分成功：成功 ${successCount}/${totalCount} 个`);
          // 部分成功保留在本页，便于用户修正后重试
        } else {
          Modal.error({
            title: '批量更新失败',
            content,
            width: 720
          });
          message.error(data?.message || '批量更新失败');
        }
      } else if (data?.success) {
        message.success(`批量更新成功，共更新 ${successCount} 个活码`);
        navigate('/wework-contact-way');
      } else {
        message.error(data?.message || '批量更新失败');
      }
    } catch (error) {
      console.error('批量更新失败:', error);
      message.error('批量更新失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wework-contact-way-page">
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
            <Breadcrumb>
                <Breadcrumb.Item onClick={() => navigate('/wework-contact-way')}>
                    <Title level={5} style={{ cursor: 'pointer', color: '#1677ff', margin: 0 }}>成员活码</Title>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <Title level={5} style={{ margin: 0 }}>批量编辑活码</Title>
                </Breadcrumb.Item>
            </Breadcrumb>
        </Col>
        <Col>
            <Space>
              <Button 
                size="middle"
                onClick={() => navigate('/wework-contact-way')}
              >
                取消
              </Button>
              <Button
                type="primary"
                onClick={() => form.submit()}
                loading={saving}
                icon={<SaveOutlined />}
                size="middle"
                disabled={selectedRowKeys.length === 0 || (!editOptions.editMembers && !editOptions.editWelcomeMsg)}
              >
                批量更新 ({selectedRowKeys.length} 个活码)
              </Button>
            </Space>
        </Col>
      </Row>

      {/* 确认待编辑活码（只读展示） */}
      <Card title="将编辑以下活码" size="small" style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8, color: '#1890ff' }}>
          共 {selectedRowKeys.length} 个活码
          <Button 
            type="link" 
            size="small" 
            onClick={() => navigate('/wework-contact-way')}
          >
            返回列表重新选择
          </Button>
        </div>
        <Table
          className="contact-way-table"
          columns={columns}
          dataSource={selectedRows && selectedRows.length > 0 ? selectedRows : []}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </Card>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="contact-way-form"
      >
        {/* 编辑选项 */}
        <Card title="选择要编辑的内容" size="small" style={{ marginBottom: 16 }}>
          <Space direction="vertical" size="middle">
            <Checkbox
              checked={editOptions.editMembers}
              onChange={(e) => handleEditOptionChange('editMembers', e.target.checked)}
            >
              <Space>
                <UserOutlined />
                <Text strong>批量编辑使用成员</Text>
              </Space>
            </Checkbox>
            
            <Checkbox
              checked={editOptions.editWelcomeMsg}
              onChange={(e) => handleEditOptionChange('editWelcomeMsg', e.target.checked)}
            >
              <Space>
                <MessageOutlined />
                <Text strong>批量编辑欢迎语</Text>
              </Space>
            </Checkbox>
          </Space>
        </Card>

        {/* 选择使用成员 */}
        {editOptions.editMembers && (
          <Card title="批量编辑使用成员" size="small" style={{ marginBottom: 16 }}>
            <Form.Item
              name="userIds"
              rules={[{ required: true, message: '请至少选择一个成员' }]}
            >
                          <EmployeeSelect
              multiple
              placeholder="请选择要使用的企业微信成员（可多选）"
              style={{ width: '40%' }}
            />
            </Form.Item>
            <Text type="secondary">
              所选活码的使用成员将统一替换为此处选择的成员。
            </Text>
          </Card>
        )}

        {/* 欢迎语设置 */}
        {editOptions.editWelcomeMsg && (
          <Card title="批量编辑欢迎语" size="small" style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 12 }}>
              <Text strong>选择需要替换的类型</Text>
            </div>
            <Space size="large" wrap style={{ marginBottom: 16 }}>
              <Checkbox
                checked={welcomeReplaceOptions.text}
                onChange={(e) => setWelcomeReplaceOptions(prev => ({ ...prev, text: e.target.checked }))}
              >
                文本
              </Checkbox>
              <Checkbox
                checked={welcomeReplaceOptions.imageGroup}
                onChange={(e) => setWelcomeReplaceOptions(prev => ({ ...prev, imageGroup: e.target.checked }))}
              >
                图片
              </Checkbox>
              <Checkbox
                checked={welcomeReplaceOptions.video}
                onChange={(e) => setWelcomeReplaceOptions(prev => ({ ...prev, video: e.target.checked }))}
              >
                视频
              </Checkbox>
              <Checkbox
                checked={welcomeReplaceOptions.miniprogram}
                onChange={(e) => setWelcomeReplaceOptions(prev => ({ ...prev, miniprogram: e.target.checked }))}
              >
                小程序
              </Checkbox>
              <Checkbox
                checked={welcomeReplaceOptions.link}
                onChange={(e) => setWelcomeReplaceOptions(prev => ({ ...prev, link: e.target.checked }))}
              >
                网页
              </Checkbox>
            </Space>
            <Row gutter={16}>
              <Col span={14}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>编辑欢迎语内容</Text>
                  <Button
                    size="small"
                    onClick={insertNickname}
                    style={{ marginLeft: '12px', fontSize: '12px', padding: '0 8px', height: '24px' }}
                  >
                    插入昵称
                  </Button>
                </div>

                <AttachmentManager
                  attachments={attachments}
                  onUpdateContent={handleUpdateAttachmentContent}
                  onAddAttachment={handleAddAttachment}
                  onRemoveAttachment={handleRemoveAttachment}
                  previewUrls={previewUrls}
                  uploading={uploading}
                  uploadProgress={uploadProgress}
                  handleFileUpload={handleFileUpload}
                  handleRemoveFile={handleRemoveFile}
                  stepType="MESSAGE"
                  isTemplateActive={false}
                  // 让素材选择器支持一次选择多张图片并正确汇入同一图片组
                  materialSelectorMultiSelect={true}
                />
              </Col>
              
              <Col span={10}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>预览效果</Text>
                </div>
                
                <div style={{ marginTop: '8px' }}>
                  {previewStep ? (
                    <MessagePreview
                      key={JSON.stringify(previewStep)}
                      step={{
                        type: 'MESSAGE',
                        actionType: 'SEND_MESSAGE',
                        messageList: attachments.map(attachment => {
                          if (attachment.type === 'TEXT') {
                            return {
                              type: 'TEXT',
                              params: { content: attachment.content || '' }
                            };
                          } else if (attachment.type === 'IMAGE_GROUP') {
                            return {
                              type: 'IMAGE_GROUP',
                              params: {
                                imageGroup: attachment.imageGroup || { mediaIds: [], ossUrls: {} }
                              }
                            };
                          } else if (attachment.type === 'VIDEO') {
                            return {
                              type: 'VIDEO',
                              params: {
                                mediaId: attachment.mediaId || '',
                                ossUrl: attachment.ossUrl || ''
                              }
                            };
                          } else if (attachment.type === 'LINK') {
                            return {
                              type: 'LINK',
                              params: {
                                title: attachment.title || '',
                                url: attachment.url || '',
                                description: attachment.description || '',
                                picUrl: attachment.picUrl || '',
                                picUrl_ossUrl: attachment.picUrl_ossUrl || ''
                              }
                            };
                          } else if (attachment.type === 'MINIPROGRAM') {
                            return {
                              type: 'MINIPROGRAM',
                              params: {
                                title: attachment.title || '',
                                appid: attachment.appid || '',
                                pagepath: attachment.pagepath || '',
                                thumbMediaId: attachment.thumbMediaId || '',
                                thumbMediaId_ossUrl: attachment.thumbMediaId_ossUrl || ''
                              }
                            };
                          } else if (attachment.type === 'FILE') {
                            return {
                              type: 'FILE',
                              params: {
                                mediaId: attachment.mediaId || '',
                                ossUrl: attachment.ossUrl || ''
                              }
                            };
                          }
                          return null;
                        }).filter(Boolean)
                      }}
                      noTruncate={true}
                    />
                  ) : (
                    <div style={{ 
                      padding: '40px', 
                      textAlign: 'center', 
                      color: '#999',
                      border: '1px dashed #d9d9d9',
                      borderRadius: '6px'
                    }}>
                      <Text type="secondary">请编辑欢迎语内容以查看预览效果</Text>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
            <Text type="secondary">
              所选活码的欢迎语将统一替换为此处设置的内容。
            </Text>
          </Card>
        )}

        {/* 提交按钮已移动到页面顶部 */}
      </Form>
    </div>
  );
};

export default WeworkContactWayBatchEdit;